import api, { type PaginatedResponse } from "./api";

export type MaintenanceStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "overdue"
  | "cancelled";

export type MaintenancePriority = "low" | "medium" | "high" | "critical";

export type MaintenanceTaskType =
  | "inspection"
  | "preventive"
  | "corrective"
  | "predictive"
  | "emergency"
  | "calibration";

export interface MaintenanceTask {
  id: string;
  asset_id: string;
  template_id?: string | null;
  task_type: string;
  description: string;
  scheduled_date: string;
  completed_date?: string | null;
  assignee_id?: string | null;
  priority: string;
  status: string;
  notes?: string | null;
  template_data?: Record<string, unknown> | null;
  metadata_?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ListMaintenanceTasksParams {
  virtual_site_id?: string;
  asset_id?: string;
  status?: MaintenanceStatus;
  page?: number;
  page_size?: number;
}

export async function listMaintenanceTasks(
  params: ListMaintenanceTasksParams,
): Promise<PaginatedResponse<MaintenanceTask>> {
  const res = await api.get("/maintenance/tasks", { params });
  return res.data;
}

export interface UpdateMaintenanceTaskPayload {
  status?: MaintenanceStatus;
  completed_date?: string;
  scheduled_date?: string;
  notes?: string;
  priority?: MaintenancePriority;
  assignee_id?: string;
  task_type?: MaintenanceTaskType;
  description?: string;
}

export async function updateMaintenanceTask(
  taskId: string,
  payload: UpdateMaintenanceTaskPayload,
): Promise<MaintenanceTask> {
  const res = await api.put(`/maintenance/tasks/${taskId}`, null, {
    params: payload,
  });
  return res.data;
}

/** Per-asset rollup of active maintenance state.
 *  - `overdue`: at least one task whose status is `overdue` OR is `scheduled`
 *    with a past `scheduled_date`. The backend stores the literal status the
 *    user last set, so we derive the past-date variant on the client (mirrors
 *    the dashboard service's behavior).
 *  - `in_progress`: at least one task in_progress (and no overdue task).
 *  - `scheduled`: a future scheduled task exists (and nothing more urgent).
 *  - null when only completed/cancelled tasks remain.
 *
 *  Sorted in worst→best priority, so the first matching bucket wins. */
export type AssetMaintenanceState =
  | "overdue"
  | "in_progress"
  | "scheduled"
  | null;

export interface AssetMaintenanceSummary {
  state: AssetMaintenanceState;
  overdueCount: number;
  inProgressCount: number;
  scheduledCount: number;
  /** The single most-relevant task to surface in compact UI (overdue > in_progress > soonest scheduled). */
  primaryTask: MaintenanceTask | null;
}

export function summarizeAssetMaintenance(
  tasks: MaintenanceTask[],
): AssetMaintenanceSummary {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let overdue: MaintenanceTask[] = [];
  let inProgress: MaintenanceTask[] = [];
  let scheduled: MaintenanceTask[] = [];

  for (const t of tasks) {
    if (t.status === "completed" || t.status === "cancelled") continue;
    if (t.status === "overdue") {
      overdue.push(t);
      continue;
    }
    if (t.status === "in_progress") {
      inProgress.push(t);
      continue;
    }
    if (t.status === "scheduled") {
      const sd = t.scheduled_date ? new Date(t.scheduled_date) : null;
      if (sd && sd < today) overdue.push(t);
      else scheduled.push(t);
    }
  }

  const sortBySched = (a: MaintenanceTask, b: MaintenanceTask) =>
    a.scheduled_date.localeCompare(b.scheduled_date);
  overdue = overdue.sort(sortBySched);
  inProgress = inProgress.sort(sortBySched);
  scheduled = scheduled.sort(sortBySched);

  const primary =
    overdue[0] ?? inProgress[0] ?? scheduled[0] ?? null;
  const state: AssetMaintenanceState =
    overdue.length > 0
      ? "overdue"
      : inProgress.length > 0
        ? "in_progress"
        : scheduled.length > 0
          ? "scheduled"
          : null;

  return {
    state,
    overdueCount: overdue.length,
    inProgressCount: inProgress.length,
    scheduledCount: scheduled.length,
    primaryTask: primary,
  };
}

/** Fetch every maintenance task for a virtual site, walking pages until done.
 *  The backend caps `page_size` at 100, so callers that need the full set
 *  (the dashboard trend, the viewer overlay) can't ask for everything in one
 *  shot. Sequential round trips are fine here — both call sites run at most
 *  once per page load. */
export async function fetchAllSiteMaintenanceTasks(
  virtualSiteId: string,
): Promise<MaintenanceTask[]> {
  const PAGE_SIZE = 100;
  const out: MaintenanceTask[] = [];
  let page = 1;
  for (;;) {
    const res = await listMaintenanceTasks({
      virtual_site_id: virtualSiteId,
      page,
      page_size: PAGE_SIZE,
    });
    out.push(...res.items);
    if (page >= res.total_pages || res.items.length === 0) break;
    page += 1;
  }
  return out;
}

/** Fetch all maintenance tasks for a virtual site and group them by asset_id.
 *  Used by the digital twin / point cloud viewers to overlay maintenance
 *  state onto tagged assets without an N+1 per-tag fetch. */
export async function fetchSiteMaintenanceByAsset(
  virtualSiteId: string,
): Promise<Map<string, AssetMaintenanceSummary>> {
  const tasks = await fetchAllSiteMaintenanceTasks(virtualSiteId);
  const byAsset = new Map<string, MaintenanceTask[]>();
  for (const t of tasks) {
    const list = byAsset.get(t.asset_id) ?? [];
    list.push(t);
    byAsset.set(t.asset_id, list);
  }
  const out = new Map<string, AssetMaintenanceSummary>();
  byAsset.forEach((tasks, assetId) => {
    out.set(assetId, summarizeAssetMaintenance(tasks));
  });
  return out;
}

/** Small style tokens used by both viewers and the maintenance page so the
 *  per-state colors stay in sync wherever a maintenance indicator renders. */
export const MAINTENANCE_STATE_TOKENS: Record<
  Exclude<AssetMaintenanceState, null>,
  { label: string; hex: string; ringHex: string; chipClass: string }
> = {
  overdue: {
    label: "Overdue",
    hex: "#ef4444",
    ringHex: "#dc2626",
    chipClass:
      "border border-red-500/40 bg-red-500/15 text-red-300",
  },
  in_progress: {
    label: "In Progress",
    hex: "#3b82f6",
    ringHex: "#2563eb",
    chipClass:
      "border border-blue-500/40 bg-blue-500/15 text-blue-300",
  },
  scheduled: {
    label: "Scheduled",
    hex: "#f59e0b",
    ringHex: "#d97706",
    chipClass:
      "border border-amber-500/40 bg-amber-500/15 text-amber-300",
  },
};
